import { useOrganizationDelete, useUserActiveOrganization } from "@/api";
import { Button } from "@/components/ui/button";
import Dialog from "@/components/ui/dialog";
import { LoadingCircle } from "@/components/ui/loadingCircle";
import { toast } from "@/components/ui/use-toast";
import { DialogClose } from "@radix-ui/react-dialog";
import { useNavigate } from "@tanstack/react-router";
import { Dispatch, SetStateAction } from "react";

interface Props {
  setOpen: Dispatch<SetStateAction<boolean>>;
}

export function OrganizationDeleteModal({ setOpen }: Props) {
  const { activeOrganization } = useUserActiveOrganization();
  const [deleteOrganization, { loading }] = useOrganizationDelete();
  const navigate = useNavigate();

  function handleDeleteOrganization() {
    if (!activeOrganization) return;

    deleteOrganization({ variables: { organizationId: activeOrganization.id } })
      .then(({ data, errors }) => {
        if (!data || errors) {
          console.error("Unable to delete organization", { data, errors });
          return toast({
            title: "Unable to delete organization",
            description:
              "An error occurred while trying to delete the organization. Please try again or report the issue.",
            variant: "destructive"
          });
        }

        const typename = data?.organizationDelete.__typename;

        if (typename === "OrganizationHasApps") {
          return toast({
            title: "An organization with apps cannot be deleted",
            description:
              "To delete the organization, first delete all the organization's apps by going to the App Gallery.",
            variant: "destructive",
            duration: 15000
          });
        }

        if (typename === "OrganizationHasInboundSubscriptions") {
          return toast({
            title: "An organization with subscriptions cannot be deleted",
            description:
              "To delete the organization, first delete all the organization's inbound subscriptions by going to the Subscriptions page.",
            variant: "destructive",
            duration: 15000
          });
        }

        if (typename === "OrganizationNotFound") {
          return toast({
            title: "Unable to delete organization",
            description:
              "An error occurred while trying to delete the organization. Please try again or report the issue.",
            variant: "destructive"
          });
        }

        toast({
          title: "Organization deleted",
          description: (
            <span>
              The organization{" "}
              <span className="font-semibold">{activeOrganization?.name}</span>{" "}
              has been deleted from Numerous.
            </span>
          )
        });

        navigate({
          to: "/organization",
          search: { reset: true }
        });

        return undefined;
      })
      .catch((error) => {
        console.error("Unable to delete organization", error);
        toast({
          title: "Unable to delete organization",
          description:
            "An error occurred while trying to delete the organization. Please try again or report the issue.",
          variant: "destructive"
        });
      });
  }

  return (
    <Dialog.Root open onOpenChange={setOpen}>
      <Dialog.Content className="max-w-lg dark:bg-brand-gray-900">
        <Dialog.Header className="space-y-2 relative">
          <Dialog.Title className="leading-5 text-brand-gray-800 dark:text-brand-gray-100 ">
            Delete organization
          </Dialog.Title>
          <Dialog.Description className="text-brand-gray-700 dark:text-brand-gray-300">
            Are you sure you want to delete{" "}
            <span className="font-semibold">{activeOrganization?.name}</span>?
            This action cannot be undone.
          </Dialog.Description>
        </Dialog.Header>

        <Dialog.Footer className="mt-8">
          <DialogClose asChild>
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant="destructive"
            disabled={loading}
            onClick={handleDeleteOrganization}
          >
            Delete
            {loading && <LoadingCircle className="ml-2" />}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
}
